import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const LOCAL_VERSION_KEY = 'app_version';

/**
 * Detect if app is running as installed PWA (standalone mode)
 * Only show update toasts for PWA users - desktop browser users can just refresh
 */
function isPWAInstalled(): boolean {
  // Check display-mode: standalone (works on most browsers)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  // Check iOS Safari standalone mode
  const isIOSStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return isStandalone || isIOSStandalone;
}

interface VersionInfo {
  version: string;
  forceUpdate?: boolean;
}

async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    // Add cache-busting query param to bypass cache
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

function getLocalVersion(): string | null {
  try {
    return localStorage.getItem(LOCAL_VERSION_KEY);
  } catch {
    return null;
  }
}

function setLocalVersion(version: string): void {
  try {
    localStorage.setItem(LOCAL_VERSION_KEY, version);
  } catch {
    // Ignore storage errors
  }
}

async function clearCachesAndReload(): Promise<void> {
  try {
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }

    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // Navigate with cache-busting param to bypass HTTP cache
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString());
    window.location.href = url.toString();
  } catch {
    // Fallback: just reload
    window.location.reload();
  }
}

export function useAppUpdate() {
  const hasChecked = useRef(false);

  useEffect(() => {
    async function checkForUpdate() {
      const serverVersion = await fetchVersion();
      if (!serverVersion) return;

      const localVersion = getLocalVersion();

      // First time: just save the version
      if (!localVersion) {
        setLocalVersion(serverVersion.version);
        return;
      }

      // Version changed
      if (localVersion !== serverVersion.version) {
        console.log(`[Update] New version available: ${serverVersion.version} (current: ${localVersion})`);

        const isPWA = isPWAInstalled();

        if (serverVersion.forceUpdate || !isPWA) {
          // Force update OR browser user: clear caches and reload immediately
          // Clear local version so the freshly loaded code re-saves it via the
          // "first time" branch — prevents stale cache from blocking updates
          toast.info('Mise à jour en cours...', { duration: 2000 });
          setTimeout(() => {
            try { localStorage.removeItem(LOCAL_VERSION_KEY); } catch { /* ignore */ }
            clearCachesAndReload();
          }, 1000);
        } else {
          // PWA with soft update: show notification with action
          toast('Nouvelle version disponible', {
            action: {
              label: 'Mettre à jour',
              onClick: () => {
                try { localStorage.removeItem(LOCAL_VERSION_KEY); } catch { /* ignore */ }
                clearCachesAndReload();
              }
            },
            duration: 10000
          });
        }
      }
    }

    // Check on mount (only once)
    if (!hasChecked.current) {
      hasChecked.current = true;
      checkForUpdate();
    }

    // Check periodically
    const interval = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);

    // Also check when app becomes visible (user switches back to PWA)
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
